import {
  CognitoIdentityProviderClient,
  AdminCreateUserCommand,
  AdminSetUserPasswordCommand,
  AdminGetUserCommand,
  AdminAddUserToGroupCommand,
  AdminCreateUserCommandInput,
} from '@aws-sdk/client-cognito-identity-provider';
import config from './../../aws-exports';
import {generateClient, GraphQLResult} from 'aws-amplify/api';
import {signIn, fetchUserAttributes, fetchAuthSession} from 'aws-amplify/auth';
const staging = {profile: 'pikadoStaging', poolId: 'eu-west-1_z5J4jlV3D'};
//const cognito = new AWS.CognitoIdentityServiceProvider();
const MAX_USERNAME_ATTEMPTS = 5;

const buildCognitoClient = async () => {
  const {credentials} = await fetchAuthSession();

  if (
    !credentials?.accessKeyId ||
    !credentials?.secretAccessKey ||
    !credentials?.sessionToken
  ) {
    throw new Error(
      'Unable to retrieve authenticated AWS credentials from the current auth session. Ensure an authenticated user with the correct IAM role is signed in.',
    );
  }

  return new CognitoIdentityProviderClient({
    region: config.aws_cognito_region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
  });
};
export const addUserToGroup = async (
  type: string,
  username: string,
  providedClient?: CognitoIdentityProviderClient,
) => {
  const groupParamsMap = {
    SCHOOL: 'Schools',
    TEACHER: 'Teachers',
    Admin: 'Admins',
    ContentCreator: 'CONTENT_CREATOR',
  };

  const groupName = groupParamsMap[type];
  if (!groupName) return;

  const cognitoClient = providedClient ?? (await buildCognitoClient());

  const command = new AdminAddUserToGroupCommand({
    UserPoolId: config.aws_user_pools_id,
    Username: username,
    GroupName: groupName,
  });

  await cognitoClient.send(command);

  if (!providedClient) {
    cognitoClient.destroy();
  }
};

const handleExistingUsername = async (
  cognitoClient: CognitoIdentityProviderClient,
  params: AdminCreateUserCommandInput,
  passwordArg: string,
) => {
  const prefix = 'a';
  const newEmail = `${prefix}${params.Username}`;
  const finalPass = `${prefix}${passwordArg}`;
  const updatedParams: AdminCreateUserCommandInput = {
    ...params,
    Username: newEmail,
    UserAttributes: params.UserAttributes?.map((attribute) => {
      if (attribute.Name === 'email') {
        return {...attribute, Value: newEmail};
      }
      if (attribute.Name === 'email_verified') {
        return {...attribute, Value: 'true'};
      }
      if (attribute.Name === 'phone_number') {
        return attribute;
      }
      return attribute;
    }),
  };

  try {
    const result = await cognitoClient.send(
      new AdminCreateUserCommand({
        UserPoolId: config.aws_user_pools_id,
        Username: updatedParams.Username,
        UserAttributes: [
          {Name: 'email', Value: updatedParams.Username},
          {Name: 'phone_number', Value: updatedParams.Username},
          {Name: 'email_verified', Value: 'true'},
          {Name: 'phone_number_verified', Value: 'true'},
        ],
        MessageAction: 'SUPPRESS',
      }),
    );

    const cognitoUserId = result.User?.Username!;

    return {result, cognitoUserId, email: updatedParams.Username, finalPass};
  } catch (err: any) {
    console.log(err, 'Abla');
    throw err;
  }
};

export async function createCognitoUser(
  email: string,
  phoneNumber: string,
  type: string,
  password = '123456',
) {
  const cognitoClient = await buildCognitoClient();
  let FinalCognitoUserId: string;
  let FinalResult: any;
  const params: AdminCreateUserCommandInput = {
    UserPoolId: config.aws_user_pools_id,
    Username: email,
    UserAttributes: [
      {Name: 'email', Value: email},
      {Name: 'phone_number', Value: phoneNumber},
      {Name: 'email_verified', Value: 'true'},
      {Name: 'phone_number_verified', Value: 'true'},
    ],
    MessageAction: 'SUPPRESS',
  };

  try {
    FinalResult = await cognitoClient.send(new AdminCreateUserCommand(params));
    FinalCognitoUserId = FinalResult?.User?.Username!;
  } catch (error: any) {
    if (error.name === 'UsernameExistsException') {
      const {result, cognitoUserId, finalPass} = await handleExistingUsername(
        cognitoClient,
        params,
        password,
      );
      FinalCognitoUserId = cognitoUserId;
      FinalResult = result;
      password = finalPass;
    } else {
      cognitoClient.destroy();
      throw error;
    }
  }

  await cognitoClient.send(
    new AdminSetUserPasswordCommand({
      UserPoolId: config.aws_user_pools_id,
      Username: FinalCognitoUserId,
      Password: password,
      Permanent: true,
    }),
  );
  if (type) {
    await addUserToGroup(type, FinalCognitoUserId, cognitoClient);
  }

  cognitoClient.destroy();
}

export const checkCognitoUser = async (emails: string[]) => {
  const cognitoClient = await buildCognitoClient();
  try {
    const duplicates = await Promise.all(
      emails.map(async (email) => {
        try {
          const user = await cognitoClient.send(
            new AdminGetUserCommand({
              UserPoolId: config.aws_user_pools_id,
              Username: email,
            }),
          );
          return user.Username ?? null;
        } catch {
          return null;
        }
      }),
    );

    return duplicates.filter((u) => u !== null);
  } finally {
    cognitoClient.destroy();
  }
};
